import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

interface Game {
  _id: string;
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  platform: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  ageRange: string;
  estimatedTime: string;
  gameType: string;
  isActive: boolean;
  isFeatured: boolean;
  popularity: number;
  rating: number;
  playCount: number;
  completionRate: number;
  averagePlayTime: number;
  bannerImage: string;
  screenshots: string[];
  demoVideo: string;
  tags: string[];
  learningObjectives: string[];
  skills: string[];
  hardware: string[];
  software: string[];
  gameMechanics: string[];
  story: string;
  rules: string[];
  scoring: string;
  progression: {
    levels: number;
    milestones: string[];
    unlockables: string[];
  };
  socialFeatures: {
    multiplayer: boolean;
    sharing: boolean;
    collaboration: boolean;
    competition: boolean;
  };
  accessibility: {
    visual: boolean;
    auditory: boolean;
    motor: boolean;
    cognitive: boolean;
  };
  setup: string[];
  gameplay: string[];
  challenges: {
    beginner: string[];
    intermediate: string[];
    advanced: string[];
  };
  achievements: {
    name: string;
    description: string;
    requirements: string;
    reward: string;
  }[];
  leaderboards: string[];
  resources: {
    tutorials: string[];
    videos: string[];
    documentation: string[];
    community: string[];
  };
  safety: string[];
  troubleshooting: {
    commonIssues: string[];
    solutions: string[];
  };
  extensions: string[];
  realWorldApplications: string[];
  educatorNotes: string;
  parentGuide: string;
  difficultyProgression: {
    level: number;
    requirements: string[];
    rewards: string[];
  }[];
  adaptiveLearning: {
    enabled: boolean;
    algorithm: string;
    personalization: string[];
  };
  analytics: {
    tracking: string[];
    metrics: string[];
    insights: string[];
  };
}

const GameSelector: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGame = async () => {
      if (!gameId) {
        setError('No game ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/highcode/games/${gameId}`);
        
        if (response.ok) {
          const gameData = await response.json();
          setGame(gameData);
        } else if (response.status === 404) {
          setError('Game not found');
        } else {
          setError('Failed to load game');
        }
      } catch (err) {
        console.error('Error fetching game:', err);
        setError('Failed to load game');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGame();
  }, [gameId]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Game Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The game you are looking for does not exist.'}</p>
          <Link
            to="/high-code/games"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Games
          </Link>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-6xl mb-4">🎮</div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {game.title}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              {game.shortDescription}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Game Info */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Game Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Game Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Difficulty:</span>
                      <span className={`px-2 py-1 rounded-full text-sm font-semibold ${
                        game.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                        game.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {game.difficulty}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Estimated Time:</span>
                      <span className="text-gray-900">{game.estimatedTime}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Learning Objectives</h3>
                  <ul className="space-y-1">
                    {game.learningObjectives.map((objective, index) => (
                      <li key={index} className="text-gray-600 flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        {objective}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Skills You'll Develop</h3>
                <div className="flex flex-wrap gap-2">
                  {game.skills.map((skill, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">🎮 How to Play</h3>
                <div className="space-y-2 text-gray-700">
                  <p>• Follow the on-screen instructions</p>
                  <p>• Complete challenges to progress</p>
                  <p>• Learn programming concepts through gameplay</p>
                  <p>• Earn points and unlock achievements</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Panel */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Ready to Play?</h3>
              <p className="text-gray-600 mb-6">
                Click the button below to start your coding adventure!
              </p>
              
              <Link
                to={`/high-code/game/${game._id}/play`}
                className="w-full bg-gradient-to-r from-green-500 to-teal-600 text-white py-4 px-6 rounded-lg font-semibold text-center block hover:from-green-600 hover:to-teal-700 transition-all duration-300 mb-4"
              >
                🚀 Start Playing
              </Link>
              
              <Link
                to="/high-code/games"
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold text-center block hover:bg-gray-700 transition-colors"
              >
                Back to Games
              </Link>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Game Tips</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">💡</span>
                  <span>Read instructions carefully</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">🎯</span>
                  <span>Take your time to understand the concepts</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">🔄</span>
                  <span>Don't be afraid to try again</span>
                </div>
                <div className="flex items-start">
                  <span className="text-yellow-500 mr-2">🏆</span>
                  <span>Have fun while learning!</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Need Help?</h3>
              <p className="text-gray-600 mb-4">
                If you get stuck, try these resources:
              </p>
              <div className="space-y-2">
                <Link
                  to="/start-building"
                  className="block text-blue-600 hover:text-blue-800 transition-colors"
                >
                  📚 Start Building Guide
                </Link>
                <Link
                  to="/all-projects"
                  className="block text-blue-600 hover:text-blue-800 transition-colors"
                >
                  🔧 Project Tutorials
                </Link>
                <Link
                  to="/high-code"
                  className="block text-blue-600 hover:text-blue-800 transition-colors"
                >
                  🏠 High Code Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameSelector;
